import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Button } from "@mui/material";
import { useState } from "react";
import Flex from "@/components/Flex";
import LoadingOverlay from "@/components/LoadingOverlay";
import StyledDialog from "@/components/StyledDialog";
import WithInfo from "@/components/WithInfo";
import SignatureInput from "@/components/signiture/SignatureInput";
import UploadSignatureButton from "@/components/signiture/UploadSignatureButton";
import { useCosignSignature } from "../hooks/use-cosign-signature";
import {
  NOTE_PREVIEW_COSIGN_BUTTON_HEIGHT,
  NOTE_PREVIEW_COSIGN_SIGNATURE_HEIGHT,
  NOTE_PREVIEW_COSIGN_SIGNATURE_WIDTH,
  NOTE_PREVIEW_LABELS,
} from "../note-preview-constant";

export interface CosignSignatureDialogProps {
  open: boolean;
  isSubmitting?: boolean;
  onCancel: () => void;
  /** Receives the drawn signature as a PNG data URL. */
  onSubmit: (signature: string) => void | Promise<void>;
}

/** The signature pad of the Sign And Lock drawer, reused for the co-signer. */
const CosignSignatureDialog = ({
  open,
  isSubmitting,
  onCancel,
  onSubmit,
}: CosignSignatureDialogProps) => {
  const signature = useCosignSignature(open);
  const [hasTriedSubmit, setHasTriedSubmit] = useState(false);
  const isSignatureMissing = hasTriedSubmit && signature.isSignEmpty();

  const submit = () => {
    setHasTriedSubmit(true);
    const dataUrl = signature.toDataUrl();
    if (signature.isSignEmpty() || !dataUrl) return;
    onSubmit(dataUrl);
  };

  return (
    <StyledDialog
      open={open}
      onClose={onCancel}
      dialogTitle={NOTE_PREVIEW_LABELS.cosignDialogTitle}
      dialogAction={
        <Button
          variant="contained"
          onClick={submit}
          disabled={isSubmitting}
          style={{ width: "120px" }}
        >
          {NOTE_PREVIEW_LABELS.coSign}
        </Button>
      }
    >
      <LoadingOverlay isLoading={!!isSubmitting} />
      <Flex flexDirection="column" gap={1.5} width="fit-content">
        <SignatureInput
          required
          label={NOTE_PREVIEW_LABELS.coSignature}
          error={isSignatureMissing}
          canvasRef={signature.canvasRef}
          w={NOTE_PREVIEW_COSIGN_SIGNATURE_WIDTH}
          h={NOTE_PREVIEW_COSIGN_SIGNATURE_HEIGHT}
        />
        <Flex justifyContent="flex-end" gap={1}>
          <WithInfo
            hideInfoIcon={signature.canImportSignature}
            title={NOTE_PREVIEW_LABELS.importSignatureUnavailable}
          >
            <Button
              disabled={!signature.canImportSignature}
              variant="outlined"
              sx={{ height: NOTE_PREVIEW_COSIGN_BUTTON_HEIGHT }}
              startIcon={<ArrowForwardIcon />}
              onClick={signature.importStored}
            >
              {NOTE_PREVIEW_LABELS.importSignature}
            </Button>
          </WithInfo>
          <UploadSignatureButton
            signPad={signature.signPad}
            canvasRef={signature.canvasRef}
            sx={{ height: NOTE_PREVIEW_COSIGN_BUTTON_HEIGHT }}
          />
          <Button
            variant="contained"
            sx={{ height: NOTE_PREVIEW_COSIGN_BUTTON_HEIGHT }}
            onClick={signature.clear}
          >
            {NOTE_PREVIEW_LABELS.clearSignature}
          </Button>
        </Flex>
      </Flex>
    </StyledDialog>
  );
};

export default CosignSignatureDialog;
